import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdAutoAwesome,
  MdClear,
  MdCheckCircle,
  MdKeyboardArrowDown,
  MdRefresh,
} from "react-icons/md";
import { SAMPLE_THEMES } from "../../customHook/BackgroundGeneration/useBackgroundGenerator";
import "./BackgroundGenerationDrawer.css";

const MAX_CHARACTERS = 500;
const MAX_NAME_CHARACTERS = 100;

const BackgroundGenerationDrawer = ({
  isOpen,
  onClose,
  onGenerate,
  defaultGender = "women",
  activeJobsCount = 0,
}) => {
  const [gender, setGender] = useState(defaultGender);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [prompt, setPrompt] = useState("");
  const [promptError, setPromptError] = useState("");
  const [selectedThemeId, setSelectedThemeId] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReset = () => {
    setName("");
    setNameError("");
    setPrompt("");
    setPromptError("");
    setSelectedThemeId(null);
    setGender(defaultGender || "women");
  };

  const isFormValid =
    Boolean(name.trim()) &&
    Boolean(prompt.trim()) &&
    !nameError &&
    !promptError &&
    !isSubmitting;

  // Safe setter that guarantees never exceeding MAX_CHARACTERS (500)
  const updatePromptSafely = (newVal) => {
    const clamped = String(newVal ?? "").slice(0, MAX_CHARACTERS);
    setPrompt(clamped);
    if (promptError && clamped.trim()) {
      setPromptError("");
    }
  };

  useEffect(() => {
    if (defaultGender) {
      setGender(defaultGender);
    }
  }, [defaultGender]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === "undefined") return null;

  const handleSelectTheme = (theme) => {
    setSelectedThemeId(theme.id);
    updatePromptSafely(theme.prompt);
    setName(theme.title || "");
    if (nameError) setNameError("");
    if (promptError) setPromptError("");
  };

  const validateNameString = (val) => {
    const trimmed = String(val || "").trim();
    if (!trimmed) return "Background name is required";
    if (trimmed.length > 100) return "Background name must be 100 characters or fewer";
    if (/[\/\\:*?"<>|]/.test(trimmed)) return 'Name cannot contain \\ / : * ? " < > |';
    return "";
  };

  const handleNameChange = (e) => {
    const val = e.target.value;
    setName(val);
    if (nameError) {
      setNameError(validateNameString(val));
    }
  };

  const handleNameBlur = () => {
    const err = validateNameString(name);
    if (err) setNameError(err);
  };

  const handlePromptBlur = () => {
    if (!prompt.trim()) {
      setPromptError("Background concept prompt is required");
    }
  };

  const handleQuickAdd = (keyword) => {
    setPrompt((prev) => {
      const current = prev.trim();
      const combined = current ? `${current}, ${keyword}` : keyword;
      const clamped = combined.slice(0, MAX_CHARACTERS);
      if (promptError && clamped.trim()) setPromptError("");
      return clamped;
    });
  };

  const handleGenerateClick = async (e) => {
    if (e) e.preventDefault();
    const nameErr = validateNameString(name);
    if (nameErr) {
      setNameError(nameErr);
      const el = document.getElementById("bg-gen-name");
      el?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }

    const finalPrompt = prompt.trim().slice(0, MAX_CHARACTERS);
    if (!finalPrompt) {
      setPromptError("Background concept prompt is required");
      return;
    }

    const matchedTheme = SAMPLE_THEMES.find((t) => t.id === selectedThemeId);
    const themeImage = matchedTheme ? matchedTheme.image : null;

    setIsSubmitting(true);
    try {
      const res = await onGenerate({
        name: name.trim(),
        prompt: finalPrompt,
        gender: gender || "unisex",
        themeId: selectedThemeId || undefined,
        aspectRatio: "3:4",
        themeImage,
      });

      if (res && res.success === false) {
        if (res.status === 409 || (res.message && res.message.toLowerCase().includes("name"))) {
          setNameError(res.message || "A background with this name already exists in your library or queue.");
          const el = document.getElementById("bg-gen-name");
          el?.scrollIntoView({ block: "center", behavior: "smooth" });
        }
        return; // KEEP DRAWER OPEN ON 409 / ERROR!
      }

      handleReset();
      if (onClose) onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="bg-gen-drawer-overlay" onClick={onClose}>
      <div
        className="bg-gen-drawer-panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gen-header px-4 py-2 sm:px-4 sm:py-2 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-800 border border-slate-200/80 flex items-center justify-center shrink-0">
              <MdAutoAwesome size={18} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm sm:text-base font-bold text-slate-900 m-0 tracking-tight">
                  Generate Custom Background
                </h2>
                {activeJobsCount > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-800 border border-slate-200 shrink-0">
                    {activeJobsCount} in queue
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-500 m-0 font-medium">
                Synthesize photorealistic studio and lifestyle backdrops
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-800 flex items-center justify-center cursor-pointer transition-all"
            aria-label="Close drawer"
          >
            <MdClose size={18} />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div className="bg-gen-body flex-1 overflow-y-auto py-4 px-4 sm:px-5 pb-20 space-y-3.5 text-left">
          {/* SECTION 1: Identity & Target Fitting */}
          <div className="bg-gen-card space-y-2.5">
            <div className="flex items-center gap-2 pb-1.5 border-b border-slate-100">
              <span className="bg-gen-step-badge">1</span>
              <span className="text-[11px] font-bold text-slate-800 tracking-wide uppercase">
                Identity & Target Fitting
              </span>
            </div>

            {/* Background Name (Mandatory) */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="bg-gen-name"
                  className="text-[10.5px] font-semibold text-slate-700 tracking-wide"
                >
                  Background Name <span className="text-red-500 font-bold">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {name && (
                    <button
                      type="button"
                      onClick={() => {
                        setName("");
                        setNameError("Background name is required");
                      }}
                      className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5 cursor-pointer bg-transparent border-none font-semibold"
                    >
                      <MdClear size={12} /> Clear
                    </button>
                  )}
                  <span
                    className={`text-[10px] font-semibold ${
                      name.length >= MAX_NAME_CHARACTERS ? "text-amber-600 font-bold" : "text-slate-400"
                    }`}
                  >
                    {name.length}/{MAX_NAME_CHARACTERS}
                  </span>
                </div>
              </div>

              <input
                type="text"
                id="bg-gen-name"
                value={name}
                maxLength={MAX_NAME_CHARACTERS}
                onChange={handleNameChange}
                onBlur={handleNameBlur}
                placeholder="e.g. Royal Heritage Courtyard, Minimalist Studio..."
                className={`bg-gen-control ${nameError ? "is-error" : ""}`}
              />
              {nameError && (
                <span className="text-red-500 text-[10px] font-medium leading-tight block">
                  {nameError}
                </span>
              )}
            </div>

            {/* Target Fitting / Gender Dropdown */}
            <div className="space-y-1">
              <label
                htmlFor="bg-gen-gender"
                className="text-[10.5px] font-semibold text-slate-700 tracking-wide"
              >
                Target Fitting / Gender <span className="text-red-500 font-bold">*</span>
              </label>
              <div className="relative">
                <select
                  id="bg-gen-gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="bg-gen-control appearance-none pr-9 cursor-pointer"
                >
                  <option value="women">Women</option>
                  <option value="men">Men</option>
                  <option value="unisex">Unisex / Studio</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                  <MdKeyboardArrowDown size={18} />
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: Concept Prompt & Lighting */}
          <div className="bg-gen-card space-y-2.5">
            <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <span className="bg-gen-step-badge">2</span>
                <label
                  htmlFor="bg-gen-prompt"
                  className="text-[11px] font-bold text-slate-800 tracking-wide uppercase cursor-pointer m-0"
                >
                  Background Concept Prompt <span className="text-red-500 font-bold">*</span>
                </label>
              </div>
              <div className="flex items-center gap-2">
                {prompt && (
                  <button
                    type="button"
                    onClick={() => {
                      setPrompt("");
                      setSelectedThemeId(null);
                      setPromptError("Background concept prompt is required");
                    }}
                    className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5 cursor-pointer bg-transparent border-none font-semibold"
                  >
                    <MdClear size={12} /> Clear
                  </button>
                )}
                <span
                  className={`text-[10px] font-semibold ${
                    prompt.length >= MAX_CHARACTERS ? "text-amber-600 font-bold" : "text-slate-400"
                  }`}
                >
                  {prompt.length}/{MAX_CHARACTERS}
                </span>
              </div>
            </div>

            <textarea
              id="bg-gen-prompt"
              rows={3}
              value={prompt}
              maxLength={MAX_CHARACTERS}
              onChange={(e) => {
                updatePromptSafely(e.target.value);
                setSelectedThemeId(null);
              }}
              onBlur={handlePromptBlur}
              placeholder="Describe your scene: architectural features, lighting mood, color palette, ground surface, time of day..."
              className={`bg-gen-textarea ${promptError ? "is-error" : ""}`}
            />
            {promptError && (
              <span className="text-red-500 text-[10px] font-medium leading-tight block">
                {promptError}
              </span>
            )}

            {/* Quick aesthetic modifiers */}
            <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
              <span className="text-[10px] font-semibold text-slate-400 mr-0.5">
                Quick Add:
              </span>
              {[
                "Seamless infinity cove",
                "Overhead softbox lighting",
                "Editorial rim lighting",
                "Reflective catwalk floor",
                "Commercial studio fill light",
              ].map((keyword) => (
                <button
                  key={keyword}
                  type="button"
                  onClick={() => handleQuickAdd(keyword)}
                  disabled={prompt.length >= MAX_CHARACTERS}
                  className="bg-gen-quick-pill"
                >
                  +{keyword}
                </button>
              ))}
            </div>
          </div>

          {/* SECTION 3: Theme Inspirations */}
          <div className="bg-gen-card space-y-2.5">
            <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <span className="bg-gen-step-badge">3</span>
                <span className="text-[11px] font-bold text-slate-800 tracking-wide uppercase">
                  Visual Styles & Themes
                </span>
                <span className="text-[10px] font-bold text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                  6 Curated
                </span>
              </div>
              {selectedThemeId && (
                <button
                  type="button"
                  onClick={() => setSelectedThemeId(null)}
                  className="text-[10px] text-slate-400 hover:text-slate-700 cursor-pointer bg-transparent border-none font-semibold transition-colors"
                >
                  Clear Selection
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 pt-1">
              {SAMPLE_THEMES.map((theme) => {
                const isSelected = selectedThemeId === theme.id;
                return (
                  <button
                    key={theme.id}
                    type="button"
                    onClick={() => handleSelectTheme(theme)}
                    className={`bg-gen-theme-card group ${isSelected ? "is-selected" : ""}`}
                  >
                    {/* Background Image with Zoom on Hover */}
                    <img
                      src={theme.image}
                      alt={theme.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />

                    {/* Gradient scrim for high readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/35 to-slate-950/20" />

                    {/* Top Row: Category Tag & Active Checkmark */}
                    <div className="relative z-10 flex items-center justify-between w-full">
                      <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-xs text-white border border-white/20 shadow-xs">
                        {theme.tag}
                      </span>
                      {isSelected ? (
                        <span className="w-4 h-4 rounded-full bg-slate-900 text-white flex items-center justify-center shadow-sm">
                          <MdCheckCircle size={13} />
                        </span>
                      ) : (
                        <span className="w-3.5 h-3.5 rounded-full bg-white/20 backdrop-blur-xs group-hover:bg-white/40 transition-colors" />
                      )}
                    </div>

                    {/* Bottom Row: Title */}
                    <div className="relative z-10 pt-1">
                      <p className="text-[11px] font-medium text-white tracking-normal leading-tight m-0 line-clamp-2">
                        {theme.title}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>


        </div>

        {/* Footer Actions */}
        <div className="bg-gen-footer px-4 py-2.5 sm:px-4 sm:py-2.5 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-8 px-3.5 flex items-center justify-center text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer border border-slate-200 bg-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={isSubmitting}
              className="h-8 px-3.5 flex items-center justify-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer border border-slate-200 bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <MdRefresh size={14} className="text-slate-400" />
              <span>Reset</span>
            </button>
          </div>

          <button
            type="button"
            onClick={handleGenerateClick}
            disabled={!isFormValid}
            className="h-8 px-4 flex items-center justify-center gap-2 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 active:scale-[0.98] rounded-lg shadow-sm hover:shadow transition-all cursor-pointer border border-transparent disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <MdAutoAwesome size={14} className={isSubmitting ? "animate-spin text-amber-300" : "text-amber-300"} />
            <span>{isSubmitting ? "Submitting..." : "Generate Background"}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BackgroundGenerationDrawer;
